import { useMemo, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { MONTH_NAMES } from "./shared";
import DayEventsDrawer from "./calendar/DayEventsDrawer";
import EventDetailsModal from "./calendar/EventDetailsModal";
import AddSlotForm from "./calendar/AddSlotForm";
import CalendarLegend from "./calendar/CalendarLegend";
import CalendarGrid from "./calendar/CalendarGrid";
import { devLog } from "@/lib/log";

const EMPTY_SLOT = {
  date: "",
  time: "10:00",
  kind: "vendor_intro_open",
  duration_minutes: 30,
  label: "",
};

/**
 * Top-level admin calendar. Owns the month cursor + the three pieces of
 * transient UI state (the "Add slot" inline form, the per-event modal, and
 * the per-day side drawer). All visual chrome lives in dedicated children
 * inside ./calendar/ — this file is just orchestration.
 */
const CalendarPanel = ({ events, onRefresh }) => {
  const [cursor, setCursor] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [adding, setAdding] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [newSlot, setNewSlot] = useState(EMPTY_SLOT);

  const eventsByDate = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (!e.date) return;
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  const goPrev = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  const submitNewSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.date || !newSlot.time || !newSlot.kind) return;
    if (newSlot.kind === "admin_held" && !newSlot.label.trim()) {
      toast("Add a label so you know what this slot is for", {
        duration: 2400,
      });
      return;
    }
    try {
      const payload = { ...newSlot, label: newSlot.label.trim() || null };
      await api.post("/admin/slots", payload);
      toast("Slot added", { duration: 2000 });
      setAdding(false);
      setNewSlot(EMPTY_SLOT);
      onRefresh();
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not add slot", {
        duration: 2800,
      });
    }
  };

  const deleteSlot = async (slotId) => {
    try {
      await api.delete(`/admin/slots/${slotId}`);
      toast("Slot removed", { duration: 2000 });
      setActiveEvent(null);
      onRefresh();
    } catch (err) {
      devLog("ld_calendar deleteSlot failed:", err?.message);
      toast("Could not remove slot", { duration: 2400 });
    }
  };

  const cancelAppointment = async (slotId) => {
    try {
      await api.patch(`/admin/slots/${slotId}/cancel`);
      toast(
        "Appointment cancelled — booker notified by email",
        { duration: 2800 }
      );
      setActiveEvent(null);
      onRefresh();
    } catch (err) {
      toast(
        err?.response?.data?.detail || "Could not cancel appointment",
        { duration: 2600 }
      );
    }
  };

  const handleDoubleClickEvent = async (ev) => {
    // Only "to book" (open) cells double-click into booked. Anything already
    // booked, admin-held, or of another kind is a no-op so we don't clobber
    // real bookings by accident.
    const openKinds = new Set(["vendor_intro_open", "couple_intro_open"]);
    if (!openKinds.has(ev.kind) || ev.booked) return;
    try {
      await api.patch(`/admin/slots/${ev.id}/book`, {
        name: "Reserved by admin",
      });
      toast(
        ev.kind === "vendor_intro_open"
          ? "Vendor slot marked booked"
          : "Couple slot marked booked",
        { duration: 1800 }
      );
      onRefresh();
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not book slot", {
        duration: 2600,
      });
    }
  };

  return (
    <div data-testid="section-calendar">
      <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
        Workspace
      </span>
      <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
        Calendar
      </h2>

      {/* Toolbar */}
      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            data-testid="cal-prev"
            aria-label="Previous month"
            className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/20 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={goNext}
            data-testid="cal-next"
            aria-label="Next month"
            className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/20 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
          >
            ›
          </button>
          <button
            type="button"
            onClick={goToday}
            data-testid="cal-today"
            className="ml-2 inline-flex h-9 px-3 items-center text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/20 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
          >
            Today
          </button>
        </div>

        <h3
          data-testid="cal-title"
          className="font-serif-display text-2xl text-[var(--ld-ink)]"
        >
          {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
        </h3>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            data-testid="cal-add-slot"
            className="inline-flex items-center gap-1.5 h-9 px-3 text-[10px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:bg-black hover:text-[#e9d59a] transition-colors"
          >
            + Slot
          </button>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
          >
            <RefreshCcw size={12} strokeWidth={1.6} />
            Refresh
          </button>
        </div>
      </div>

      {adding && (
        <AddSlotForm
          newSlot={newSlot}
          setNewSlot={setNewSlot}
          onSubmit={submitNewSlot}
          onCancel={() => setAdding(false)}
        />
      )}

      <CalendarLegend />

      <CalendarGrid
        cursor={cursor}
        eventsByDate={eventsByDate}
        onOpenDay={setActiveDay}
        onOpenEvent={setActiveEvent}
        onDoubleClickEvent={handleDoubleClickEvent}
      />

      {activeEvent && (
        <EventDetailsModal
          event={activeEvent}
          onClose={() => setActiveEvent(null)}
          onDelete={deleteSlot}
          onCancel={cancelAppointment}
        />
      )}

      {activeDay && (
        <DayEventsDrawer
          iso={activeDay.iso}
          events={activeDay.events}
          onClose={() => setActiveDay(null)}
          onOpenEvent={(ev) => {
            setActiveDay(null);
            setActiveEvent(ev);
          }}
          onDoubleClickEvent={(ev) => {
            handleDoubleClickEvent(ev);
            setActiveDay(null);
          }}
          onAddSlotForDay={(iso) => {
            setNewSlot((s) => ({ ...s, date: iso }));
            setActiveDay(null);
            setAdding(true);
          }}
        />
      )}
    </div>
  );
};

export default CalendarPanel;
